// RulerManager — manages placed measurement rulers as Three.js overlays + DOM labels

import * as THREE from 'three'
import { DPM } from '@/constants/coordinates'
import { formatLength } from '@/utils/areaCalculation'

interface Ruler {
  id: string
  start: { x: number; y: number }  // editor coords (dots)
  end: { x: number; y: number }
  group: THREE.Group
  label: HTMLElement
}

let rulerIdCounter = 0

/** Build a thin rectangular mesh between two 3D points (visible unlike THREE.Line which is 1px) */
function createLineMesh(from: THREE.Vector3, to: THREE.Vector3, color: number, width: number): THREE.Mesh {
  const dir = new THREE.Vector3().subVectors(to, from)
  const length = dir.length()
  if (length < 0.001) {
    // Degenerate — return tiny invisible mesh
    const g = new THREE.PlaneGeometry(0.001, 0.001)
    return new THREE.Mesh(g, new THREE.MeshBasicMaterial({ visible: false }))
  }
  dir.normalize()

  // Create a plane lying flat (XZ), then orient it
  const geom = new THREE.PlaneGeometry(length, width)
  const mat = new THREE.MeshBasicMaterial({
    color,
    side: THREE.DoubleSide,
    depthTest: false,
    transparent: true,
    opacity: 0.85,
  })
  const mesh = new THREE.Mesh(geom, mat)

  // Position at midpoint
  mesh.position.set(
    (from.x + to.x) / 2,
    (from.y + to.y) / 2,
    (from.z + to.z) / 2
  )

  // Rotate to lie on XZ plane and point from→to
  mesh.rotation.x = -Math.PI / 2
  const angle = Math.atan2(dir.z, dir.x)
  mesh.rotation.z = -angle

  mesh.renderOrder = 999
  return mesh
}

export { createLineMesh }

export class RulerManager {
  private scene: THREE.Scene
  private container: HTMLElement
  private canvas: HTMLCanvasElement
  private rulers: Map<string, Ruler> = new Map()
  private labelContainer: HTMLElement
  private animFrameId: number | null = null

  constructor(scene: THREE.Scene, container: HTMLElement, canvas: HTMLCanvasElement) {
    this.scene = scene
    this.container = container
    this.canvas = canvas

    // Create a container for ruler labels
    this.labelContainer = document.createElement('div')
    this.labelContainer.className = 'dimension-overlay'
    this.labelContainer.style.pointerEvents = 'none'
    this.container.appendChild(this.labelContainer)
  }

  addRuler(start: { x: number; y: number }, end: { x: number; y: number }): string {
    const id = `ruler-${++rulerIdCounter}`
    const y = 0.05

    const startWorld = new THREE.Vector3(start.x / DPM, y, start.y / DPM)
    const endWorld = new THREE.Vector3(end.x / DPM, y, end.y / DPM)

    const group = new THREE.Group()
    group.name = id

    // Visible line (mesh-based, not THREE.Line which is 1px)
    const lineMesh = createLineMesh(startWorld, endWorld, 0xff6b6b, 0.03)
    group.add(lineMesh)

    // Endpoint spheres
    const dotGeom = new THREE.SphereGeometry(0.05, 8, 8)
    const dotMat = new THREE.MeshBasicMaterial({ color: 0xff6b6b, depthTest: false })
    const dot1 = new THREE.Mesh(dotGeom, dotMat)
    dot1.position.copy(startWorld)
    dot1.renderOrder = 999
    group.add(dot1)

    const dot2 = new THREE.Mesh(dotGeom, dotMat)
    dot2.position.copy(endWorld)
    dot2.renderOrder = 999
    group.add(dot2)

    this.scene.add(group)

    // DOM label
    const label = document.createElement('div')
    label.className = 'dimension-label'
    const dx = endWorld.x - startWorld.x
    const dz = endWorld.z - startWorld.z
    const distance = Math.sqrt(dx * dx + dz * dz)
    label.textContent = formatLength(distance)
    this.labelContainer.appendChild(label)

    const ruler: Ruler = { id, start, end, group, label }
    this.rulers.set(id, ruler)

    return id
  }

  removeRuler(id: string): void {
    const ruler = this.rulers.get(id)
    if (!ruler) return

    // Dispose Three.js objects
    ruler.group.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry?.dispose()
        if (child.material instanceof THREE.Material) child.material.dispose()
      }
    })
    this.scene.remove(ruler.group)

    // Remove DOM label
    ruler.label.remove()

    this.rulers.delete(id)
  }

  clearAll(): void {
    for (const id of [...this.rulers.keys()]) {
      this.removeRuler(id)
    }
  }

  getRulerCount(): number {
    return this.rulers.size
  }

  /** Update all label positions based on current camera. Call from render loop. */
  updateLabels(camera: THREE.Camera): void {
    const rect = this.canvas.getBoundingClientRect()

    for (const ruler of this.rulers.values()) {
      const y = 0.05
      const startWorld = new THREE.Vector3(ruler.start.x / DPM, y, ruler.start.y / DPM)
      const endWorld = new THREE.Vector3(ruler.end.x / DPM, y, ruler.end.y / DPM)

      // Position label at midpoint
      const mid = new THREE.Vector3(
        (startWorld.x + endWorld.x) / 2,
        y,
        (startWorld.z + endWorld.z) / 2
      )
      mid.project(camera)

      if (mid.z < -1 || mid.z > 1) {
        ruler.label.classList.remove('visible')
        continue
      }

      const x = (mid.x * 0.5 + 0.5) * rect.width
      const sy = (-mid.y * 0.5 + 0.5) * rect.height

      ruler.label.style.left = `${x}px`
      ruler.label.style.top = `${sy}px`
      ruler.label.classList.add('visible')
    }
  }

  /** Start a rAF loop that keeps labels positioned. */
  startLabelLoop(getCamera: () => THREE.Camera | null): void {
    const tick = () => {
      const camera = getCamera()
      if (camera && this.rulers.size > 0) {
        this.updateLabels(camera)
      }
      this.animFrameId = requestAnimationFrame(tick)
    }
    this.animFrameId = requestAnimationFrame(tick)
  }

  dispose(): void {
    if (this.animFrameId !== null) {
      cancelAnimationFrame(this.animFrameId)
      this.animFrameId = null
    }
    this.clearAll()
    this.labelContainer.remove()
  }
}
